import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ErrorIntercept } from '@mysrc/interceptors/error.interceptor';
import { InterceptorDateHttp } from '@mysrc/interceptors/interceptorDateHttp';
import { LoaderInterceptor } from '@mysrc/interceptors/interceptorLoader';
import { NgbModule, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { NotificationService } from '@progress/kendo-angular-notification';
import { LicenseServiceGrid, RoleTemplateServiceGrid } from './features/admin';
import {
  UserService,
  ApplicationInsightsErrorHandler,
  UiWindowService,
  ConfirmationDialogService,
  ConfirmationDialogHtmlPaneService,
} from './modules/fwcore';
import { initSettingsTabs } from './modules/fwcore/components/user-settings/user-settings.func';
import { FwcoreModule } from './modules/fwcore/fwcore.module';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { AppEffects } from './store/app.effects';
import { reducers, metaReducers } from './store/reducer';
import { provideStoreDevtools } from '@ngrx/store-devtools';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      OktaAuthModule,
      NgbModule,
      NgbAlertModule,
      FwcoreModule,
      NgIdleKeepaliveModule.forRoot()
    ),
    {
      provide: OKTA_CONFIG,
      useFactory: (config: 'CONFIG_SETTINGS') => {
        const oktaSettings = config[<any>'okta'];
        const oktaConfig = {
          issuer: oktaSettings[<any>'issuer'],
          redirectUri: oktaSettings[<any>'redirectUri'],
          postLogoutRedirectUri: oktaSettings[<any>'postLogoutRedirectUri'],
          clientId: oktaSettings[<any>'clientId'],
          scopes: Array.from(oktaSettings[<any>'scopes']),
          pkce: JSON.parse(oktaSettings[<any>'pkce']),
        };
        const oktaAuth = new OktaAuth(oktaConfig);
        return { oktaAuth };
      },
      deps: ['CONFIG_SETTINGS'],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorDateHttp,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorIntercept,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSettingsTabs,
      deps: [UserService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ApplicationInsightsErrorHandler,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    UiWindowService,
    NotificationService,
    ConfirmationDialogService,
    ConfirmationDialogHtmlPaneService,
    LicenseServiceGrid,
    RoleTemplateServiceGrid,
    provideRouter(routes),
    provideStore(reducers, { metaReducers }),
    provideEffects(AppEffects),
    provideHotToastConfig({
      'position': 'top-right'
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: false,
      trace: false,
      traceLimit: 75,
      connectInZone: true
    })
  ],
};
